import { ApplicationView, raw, useController } from '@aha-app/mvc';
import React from 'react';
import { result } from 'lodash';
import ObjectMenuController from './ObjectMenuController';
import Section from './ObjectMenuSection';
import SectionButton from './SectionButton';
import ToolButton from '../../toolbars/ToolButton';
import classNames from 'classnames';
import { stylusComponent } from '../../utils/stylus';
import WithHint from '../../toolbars/WithHint';
import styles from './objectMenuStyles.module.css';
import { handlePromoClick, testId } from '../../utils/objectMenu';
import { type TabProps } from './ObjectMenu';
import type StylusWireframe from '../../stylus/StylusWireframe';
import StylusShape from '../../stylus/StylusShape';
import { TOOLTIP_DELAY_GENERAL } from '../../constants';
import type StylusTable from '../../stylus/StylusTable';
import { itemUniqueId } from './SearchManager';
import type StylusDevice from '../../stylus/StylusDevice';
import WhiteboardController from '../../controllers/WhiteboardController';
import {
  isMenuItemDefinition,
  type MenuItemDefinition,
} from '../../stylus/definitions';
import type StylusFlow from '../../stylus/StylusFlow';
import type StylusSwimlane from '../../stylus/StylusSwimlane';
import { type PromoLinkType, type PromoLinkValue } from '../../shared-types';
import StylusCustomShape from '../../stylus/StylusCustomShape';
import StylusMindMap from '../../stylus/StylusMindMap';

export type MenuStylus =
  | StylusShape
  | StylusFlow
  | StylusSwimlane
  | StylusWireframe
  | StylusDevice
  | StylusCustomShape
  | StylusTable
  | StylusMindMap;

export type ShapeTabItem = MenuStylus | MenuItemDefinition;

// This is really the content of the tab, not the tab component in the header itself
const ShapesTab: React.FC<TabProps> = ({ onCollapseToggle }) => {
  const whiteboardController = useController(WhiteboardController);
  const controller = useController(ObjectMenuController);
  const { shapeDragManager } = whiteboardController;
  const { backButtonVisible, topLevel } = controller;

  return (
    <div data-drag-abort>
      {controller
        .activeSections<ShapeTabItem>()
        .map(
          ({
            title,
            titleElement,
            items,
            sections,
            tooltip,
            promoLink = '' as PromoLinkType,
            showNew = false,
            promoTooltipSelector = '',
          }) => {
            const promoLinkVal = result<PromoLinkValue>(
              { promoLink },
              'promoLink'
            );

            return (
              <Section
                key={title}
                title={title}
                titleElement={titleElement ? titleElement() : undefined}
                promoLink={promoLink}
                showNew={showNew}
                promoTooltipSelector={promoTooltipSelector}
                onCollapseToggle={onCollapseToggle}
              >
                {items.map(item => {
                  const key = itemUniqueId(item);
                  const isDefinition = isMenuItemDefinition(item);
                  let isDraggable = !promoLinkVal;
                  isDraggable &&= !(
                    item instanceof StylusShape && item.nonBrushShape()
                  );

                  const stylusClass = item.toolButtonClass?.();
                  const Button = (
                    <ToolButton
                      className={classNames(stylusClass, 'w-full')}
                      draggable={isDraggable}
                      onDragStart={e =>
                        queueMicrotask(() =>
                          shapeDragManager.startDrag(e, item)
                        )
                      }
                      onClick={event => {
                        event.stopPropagation();
                        if (promoLinkVal) {
                          handlePromoClick(promoLinkVal);
                        } else {
                          const stylusToSet = isDefinition
                            ? whiteboardController.getStylus(
                                item.stylusDefinition
                              )
                            : raw(item);

                          controller.actionCloseMenu(stylusToSet);
                        }
                      }}
                      testId={
                        item instanceof StylusMindMap
                          ? `Button-${item.testid()}`
                          : testId(item.description())
                      }
                      onHoverMenu={
                        item instanceof StylusCustomShape
                          ? item.onHoverMenu
                          : undefined
                      }
                    >
                      {isDefinition ? item.renderIcon() : stylusComponent(item)}
                    </ToolButton>
                  );
                  return tooltip ? (
                    <WithHint
                      key={key}
                      name={item.description()}
                      delay={[TOOLTIP_DELAY_GENERAL, 0]}
                    >
                      {Button}
                    </WithHint>
                  ) : (
                    <div key={key}>{Button}</div>
                  );
                })}

                {!!sections.length && (
                  <SectionButton
                    title={`All ${title.toLowerCase()}`}
                    onClick={() => {
                      if (promoLinkVal) {
                        handlePromoClick(promoLinkVal);
                      } else {
                        controller.actionPushSections(sections);
                      }
                    }}
                  />
                )}
              </Section>
            );
          }
        )}
      {backButtonVisible && !topLevel && (
        <SectionButton
          className={styles.objectMenuSectionButtonBackButton}
          title='All shapes'
          onClick={() => controller.actionPopSections()}
        />
      )}
    </div>
  );
};

export default ApplicationView(ShapesTab);
